// ---------------------------------------------------------------------------
// Assessment service — modern UI primitives.
//
// A small, dependency-free (MUI v4 + SVG + CSS) design layer that gives the
// assessment surfaces a contemporary feel: motion on mount, animated numbers,
// an SVG maturity gauge, a segmented control, glass cards, toasts, a confirm
// dialog and an error boundary. Everything honors prefers-reduced-motion.
// ---------------------------------------------------------------------------

import {
  Component,
  createContext,
  CSSProperties,
  ErrorInfo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
} from '@material-ui/core';
import { q } from '../palette';

// --- reduced motion ---------------------------------------------------------

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// --- keyframes + shared classes ---------------------------------------------

const useMotionStyles = makeStyles({
  '@keyframes qFadeUp': {
    from: { opacity: 0, transform: 'translateY(14px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  '@keyframes qFadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  '@keyframes qShimmer': {
    '0%': { backgroundPosition: '-400px 0' },
    '100%': { backgroundPosition: '400px 0' },
  },
  reveal: {
    animation: '$qFadeUp 520ms cubic-bezier(0.16,1,0.3,1) both',
    '@media (prefers-reduced-motion: reduce)': { animation: '$qFadeIn 1ms both' },
  },
});

/** Fade + rise in on mount, with an optional stagger delay (ms). */
export function Reveal({
  children,
  delay = 0,
  className,
  style,
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  style?: CSSProperties;
}) {
  const c = useMotionStyles();
  return (
    <div
      className={`${c.reveal} ${className ?? ''}`}
      style={{ animationDelay: `${delay}ms`, ...style }}
    >
      {children}
    </div>
  );
}

// --- animated number --------------------------------------------------------

/** Count-up number; jumps instantly under reduced-motion. */
export function AnimatedNumber({
  value,
  decimals = 0,
  duration = 780,
  prefix = '',
  suffix = '',
}: {
  value: number;
  decimals?: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
}) {
  const [display, setDisplay] = useState(prefersReducedMotion() ? value : 0);
  const raf = useRef<number>();
  const from = useRef(0);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setDisplay(value);
      return undefined;
    }
    const start = performance.now();
    const startVal = from.current;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = startVal + (value - startVal) * eased;
      setDisplay(v);
      if (p < 1) raf.current = requestAnimationFrame(tick);
      else from.current = value;
    };
    raf.current = requestAnimationFrame(tick);
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
      from.current = value;
    };
  }, [value, duration]);

  return (
    <>
      {prefix}
      {display.toFixed(decimals)}
      {suffix}
    </>
  );
}

// --- gauge ring -------------------------------------------------------------

/** Circular SVG gauge showing value/max, with an animated arc. */
export function GaugeRing({
  value,
  max = 5,
  size = 148,
  stroke = 12,
  color = q.emerald,
  decimals = 1,
  label,
  sublabel,
}: {
  value: number;
  max?: number;
  size?: number;
  stroke?: number;
  color?: string;
  decimals?: number;
  label?: ReactNode;
  sublabel?: ReactNode;
}) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, max ? value / max : 0));
  const [offset, setOffset] = useState(circ);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setOffset(circ * (1 - pct));
      return undefined;
    }
    const id = requestAnimationFrame(() => setOffset(circ * (1 - pct)));
    return () => cancelAnimationFrame(id);
  }, [circ, pct]);

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={q.border} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 900ms cubic-bezier(0.16,1,0.3,1)' }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <div
          style={{
            fontFamily: "'Clash Display', Georgia, serif",
            fontWeight: 700,
            fontSize: size * 0.3,
            lineHeight: 1,
            color,
          }}
        >
          <AnimatedNumber value={value} decimals={decimals} />
        </div>
        {label && (
          <div style={{ fontSize: 11, color: q.slate, letterSpacing: '0.08em' }}>{label}</div>
        )}
        {sublabel && <div style={{ fontSize: 10, color: q.slateDim }}>{sublabel}</div>}
      </div>
    </div>
  );
}

// --- segmented control ------------------------------------------------------

const useSegStyles = makeStyles({
  wrap: {
    display: 'inline-flex',
    padding: 4,
    gap: 4,
    background: q.surface2,
    border: `1px solid ${q.border}`,
    borderRadius: 12,
  },
  seg: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 14px',
    borderRadius: 9,
    border: 'none',
    background: 'transparent',
    color: q.slate,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    transition: 'all .18s ease',
    '&:hover': { color: q.ghost },
  },
  active: {
    background: 'linear-gradient(135deg, rgba(16,185,129,0.22), rgba(6,182,212,0.12))',
    color: q.ghost,
    boxShadow: `inset 0 0 0 1px ${q.emerald}55`,
  },
});

export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: ReactNode; icon?: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
}) {
  const c = useSegStyles();
  return (
    <div className={c.wrap} role="tablist">
      {options.map(o => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={o.value === value}
          className={`${c.seg} ${o.value === value ? c.active : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.icon}
          {o.label}
        </button>
      ))}
    </div>
  );
}

// --- glass card -------------------------------------------------------------

const useCardStyles = makeStyles({
  card: {
    position: 'relative',
    background: `linear-gradient(180deg, ${q.surface}, ${q.surface2})`,
    border: `1px solid ${q.border}`,
    borderRadius: 16,
    overflow: 'hidden',
  },
  interactive: {
    transition: 'transform .2s ease, border-color .2s ease, box-shadow .2s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-3px)',
      borderColor: `${q.emerald}66`,
      boxShadow: '0 12px 32px -18px rgba(16,185,129,0.55)',
    },
  },
  accentBar: { position: 'absolute', top: 0, left: 0, right: 0, height: 3 },
});

export function GlassCard({
  children,
  accent,
  interactive,
  onClick,
  className,
  style,
}: {
  children: ReactNode;
  accent?: string;
  interactive?: boolean;
  onClick?: () => void;
  className?: string;
  style?: CSSProperties;
}) {
  const c = useCardStyles();
  return (
    <div
      className={`${c.card} ${interactive ? c.interactive : ''} ${className ?? ''}`}
      onClick={onClick}
      style={style}
    >
      {accent && <div className={c.accentBar} style={{ background: accent }} />}
      {children}
    </div>
  );
}

// --- animated progress bar --------------------------------------------------

export function ProgressBar({
  value,
  color = q.emerald,
  height = 8,
  track = q.border,
}: {
  value: number; // 0..100
  color?: string;
  height?: number;
  track?: string;
}) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div style={{ height, borderRadius: 999, background: track, overflow: 'hidden', width: '100%' }}>
      <div
        style={{
          height: '100%',
          width: `${pct}%`,
          borderRadius: 999,
          background: `linear-gradient(90deg, ${color}, ${q.glow})`,
          transition: 'width 800ms cubic-bezier(0.16,1,0.3,1)',
        }}
      />
    </div>
  );
}

// --- empty state ------------------------------------------------------------

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        padding: '40px 24px',
        textAlign: 'center',
      }}
    >
      {icon && (
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(16,185,129,0.12)',
            color: q.emerald,
          }}
        >
          {icon}
        </div>
      )}
      <div style={{ color: q.ghost, fontWeight: 700, fontSize: 16 }}>{title}</div>
      {description && (
        <div style={{ color: q.slate, fontSize: 13, maxWidth: 420 }}>{description}</div>
      )}
      {action && <div style={{ marginTop: 6 }}>{action}</div>}
    </div>
  );
}

// --- confirm dialog ---------------------------------------------------------

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  danger,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ style: dialogPaper }}>
      <DialogTitle>
        <span style={{ color: q.ghost, fontWeight: 700 }}>{title}</span>
      </DialogTitle>
      <DialogContent>
        <div style={{ color: q.slate, fontSize: 14 }}>{message}</div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} style={{ color: q.slate }}>
          {cancelLabel}
        </Button>
        <Button
          onClick={() => {
            onConfirm();
            onClose();
          }}
          style={{
            color: '#fff',
            background: danger ? q.red : q.emerald,
            fontWeight: 700,
            padding: '6px 16px',
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const dialogPaper: CSSProperties = {
  background: q.surface,
  border: `1px solid ${q.border}`,
  borderRadius: 16,
  color: q.ghost,
};

// --- toasts -----------------------------------------------------------------

type ToastTone = 'success' | 'error' | 'info';
interface ToastItem {
  id: string;
  message: string;
  tone: ToastTone;
}
type ToastFn = (message: string, tone?: ToastTone) => void;

const ToastCtx = createContext<ToastFn>(() => undefined);

export function useToast(): ToastFn {
  return useContext(ToastCtx);
}

const toneColor: Record<ToastTone, string> = {
  success: q.emerald,
  error: q.red,
  info: q.cyan,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const toast = useCallback<ToastFn>((message, tone = 'success') => {
    const id = `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setItems(prev => [...prev, { id, message, tone }]);
    window.setTimeout(() => {
      setItems(prev => prev.filter(t => t.id !== id));
    }, 3400);
  }, []);

  return (
    <ToastCtx.Provider value={toast}>
      {children}
      <div
        style={{
          position: 'fixed',
          right: 20,
          bottom: 20,
          zIndex: 1400,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          pointerEvents: 'none',
        }}
      >
        {items.map(t => (
          <Reveal key={t.id}>
            <div
              style={{
                minWidth: 220,
                maxWidth: 360,
                padding: '12px 16px',
                borderRadius: 12,
                background: q.surface2,
                border: `1px solid ${toneColor[t.tone]}55`,
                borderLeft: `3px solid ${toneColor[t.tone]}`,
                color: q.ghost,
                fontSize: 13,
                boxShadow: '0 12px 30px -12px rgba(0,0,0,0.7)',
              }}
            >
              {t.message}
            </div>
          </Reveal>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

// --- error boundary ---------------------------------------------------------

interface EBProps {
  children: ReactNode;
  title?: string;
}
interface EBState {
  error: Error | null;
}

export class ErrorBoundary extends Component<EBProps, EBState> {
  state: EBState = { error: null };

  static getDerivedStateFromError(error: Error): EBState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[assessments] render error', error, info);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
          <GlassCard style={{ maxWidth: 460, padding: 28, textAlign: 'center' }}>
            <div style={{ color: q.ghost, fontWeight: 700, fontSize: 18, marginBottom: 8 }}>
              {this.props.title ?? 'Algo saiu do previsto'}
            </div>
            <div style={{ color: q.slate, fontSize: 13, marginBottom: 18 }}>
              Esta tela encontrou um erro ao renderizar. Seus dados estão salvos localmente e
              intactos.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <Button
                onClick={() => this.setState({ error: null })}
                style={{ color: q.emerald, fontWeight: 700 }}
              >
                Tentar novamente
              </Button>
              <Button
                onClick={() => window.location.reload()}
                style={{ color: q.slate }}
              >
                Recarregar
              </Button>
            </div>
          </GlassCard>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Convenience: wrap a page in the error boundary + toast provider. */
export function AssessmentProviders({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <ToastProvider>{children}</ToastProvider>
    </ErrorBoundary>
  );
}
